/**
 * SSO service — social-login provider management
 */

import { apiEndpoints } from "@/src/config/api/endpoints";

import { api, getApiBaseUrl } from "./base_service";
import type { ApiError, Paginated, Result } from "./types";

/** A connectable provider as the backend sends it (snake_case). */
export interface SocialAppRaw {
  name: string;
  icon: string;
  connect_url: string;
}

/** A connectable provider at the camelCase boundary. */
export interface SocialApp {
  name: string;
  /** Server-supplied provider logo URL. */
  icon: string;
  /** Browser-redirect target that begins the OAuth connect handshake. */
  connectUrl: string;
}

/** A connected social account as the backend sends it. */
export interface SocialAccountRaw {
  id: string;
  provider: SocialAppRaw;
}

/** A connected social account at the camelCase boundary. */
export interface SocialAccount {
  /** Non-empty when connected; needed to disconnect. */
  id: string;
  provider: SocialApp;
}

function resolveConnectUrl(rawUrl: string): string {
  if (!rawUrl) return "";
  try {
    return new URL(rawUrl, getApiBaseUrl()).toString();
  } catch {
    return rawUrl;
  }
}

export function adaptSocialApp(raw: SocialAppRaw): SocialApp {
  return {
    name: raw.name ?? "",
    icon: raw.icon ?? "",
    connectUrl: resolveConnectUrl(raw.connect_url ?? ""),
  };
}

export function adaptSocialAccount(raw: SocialAccountRaw): SocialAccount {
  return {
    id: String(raw.id ?? ""),
    provider: adaptSocialApp(raw.provider ?? ({} as SocialAppRaw)),
  };
}

export async function listSocialApps(): Promise<Result<SocialApp[]>> {
  try {
    const data = await api.get<Paginated<SocialAppRaw> | SocialAppRaw[]>(
      apiEndpoints.sso().socialApplications(),
    );
    const list = Array.isArray(data) ? data : (data.results ?? []);
    return { ok: true, value: list.map(adaptSocialApp) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function listSocialAccounts(): Promise<Result<SocialAccount[]>> {
  try {
    const data = await api.get<Paginated<SocialAccountRaw> | SocialAccountRaw[]>(
      apiEndpoints.sso().socialAccounts().list(),
    );
    const list = Array.isArray(data) ? data : (data.results ?? []);
    return { ok: true, value: list.map(adaptSocialAccount) };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}

export async function deleteSocialAccount(id: string): Promise<Result<void>> {
  try {
    await api.delete<void>(apiEndpoints.sso().socialAccounts().delete(id));
    return { ok: true, value: undefined };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
